import React, { Suspense, lazy, useEffect } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import { ContentSection, Sentiment, Spinner, Toast } from 'sidecar-components'
import { GlobalNav } from 'sidecar-member-components'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  classify,
  parseBool,
  stateMapping,
  webStorage,
} from 'sidecar-utilities'
import { pathOr, isEmpty, equals, and } from 'ramda'
import { datadogLogs } from '@datadog/browser-logs'
import { connect } from 'react-redux'

import { HelpNav } from 'components/_common/HelpNav'
import { fetchAccountMetaData } from 'store/Account'
import { fetchInitialSetup } from 'store/App'
import { fetchApplicants } from 'store/Applicant'
import { logout, loginFromLocal, forceLogout } from 'store/Auth'
import { fetchGroupInfo } from 'store/Group'
import { getCurrentLocation } from 'store/Location'
import { fetchAuthedUser, fetchMembers } from 'store/Member'
import {
  clearNewMessage,
  clearRequestType,
  fetchUnresolvedMessages,
  clearPopTartHistory,
  prevHistory,
} from 'store/MemberCommunication'

import { AccessibeTrigger } from 'components/_common/AccessibeTrigger'
import { ErrorBoundary } from 'components/_common/ErrorBoundary'
import { PrivateRoutes } from 'components/_common/PrivateRoutes'
import ScrollToTop from 'components/_common/ScrollToTop'
import NotFound from 'pages/NotFound'
import NotAvailable from 'pages/NotAvailable'
import { useChatWidget } from 'utils/chat/useChatWidget'
import { useAccessibe } from 'hooks/useAccessibe'
import { useCrossTabAuthSync } from 'hooks/useCrossTabAuthSync'
import {
  setImpersonationStorage,
  cleanImpersonationParams,
  handle3rdPartyScripts,
  getGlobalNavItems,
  getNavShapeKey,
  cacheNavShape,
} from './utils'
import { useSidekick } from 'hooks/useSidekick'
import { SidekickPoptart } from 'components/sidekick/SidekickPoptart'
import { MemberCommsPoptart } from 'components/memberComms/MemberCommsPoptart'
import { usePoptartPositioning } from 'hooks/usePoptartPositioning'

// Pages (alphabetical)
const Account = lazy(() => import('pages/Account'))
const BenefitCard = lazy(() => import('pages/BenefitCard'))
const Bundles = lazy(() => import('pages/CareV2/bundles'))
const CareV2 = lazy(() => import('pages/CareV2'))
const Dashboard = lazy(() => import('pages/Dashboard'))
const Doctors = lazy(() => import('pages/Doctors'))
const Expenses = lazy(() => import('pages/Expenses'))
const ForgotPassword = lazy(() => import('pages/ForgotPassword'))
const Help = lazy(() => import('pages/Help/Help'))
const IdCard = lazy(() => import('pages/IdCard'))
const Inbox = lazy(() => import('pages/Inbox'))
const MobileAccountMenu = lazy(() => import('pages/MobileAccountMenu'))
const PharmacyDetail = lazy(() => import('pages/PharmacyDetail'))
const PreviewCoverage = lazy(() => import('pages/PreviewCoverage'))

const Login = lazy(() => import('pages/Login'))
const Logout = lazy(() => import('pages/Logout'))
const PrintCoverage = lazy(() => import('pages/PrintCoverage'))
const ResetPassword = lazy(() => import('pages/ResetPassword'))
const ThankYou = lazy(() => import('pages/ThankYou'))
const RegistrationSuccess = lazy(
  () => import('flows/_steps/RegistrationSuccess'),
)

// Flows (alphabetical)
const LinkPaymentMethod = lazy(() => import('flows/_steps/LinkPayment'))
const ReinstateFlow = lazy(() => import('flows/Reinstate'))
const RegisterAccountFlow = lazy(() => import('flows/RegisterAccount'))

export const BaseApp = ({
  accountMetadata,
  policy,
  selectedMemberUuid,
  fetchAccountMetaData,
  fetchAuthedUser,
  fetchInitialSetup,
  fetchMembers,
  fetchUnresolvedMessages,
  forceLogout,
  getCurrentLocation,
  loginFromLocal,
  logout,
  toasts,
  token,
  unresolvedRequests,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const flags = useFlags()
  const canViewExpenses = flags['impersonate-compliance-expenses']

  const [searchParams] = useSearchParams()
  const isNativeApp = searchParams.get('webview') === 'true'

  // This loads and enables the Accessibe accessibility widget
  useAccessibe()

  const { setSubscribers } = useChatWidget()

  // Set up Genesys chat widget subscribers once on mount
  useEffect(() => {
    setSubscribers()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect, setSubscribers is stable
  }, [])

  // This sets up the app to interact with the virtual assistant.
  // The listener is now automatically set up in the SidekickProvider,
  // so we just need to call the hook to get access to the functions.
  useSidekick()

  // When this component mounts, it's imperative to make sure that the
  // Auth.authToken is available in the store. This is important because
  // there are several places that use the Auth.authToken and we only ever
  // save that when a person logs in. This action makes sure that the
  // authToken is available in this store for those other functions (like
  // initing LaunchDarkly) even after page refresh.
  useEffect(() => {
    loginFromLocal()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect, loginFromLocal is stable
  }, [])

  // Follow sign-ins and sign-outs made in other tabs.
  useCrossTabAuthSync()

  // Poptart positioning hook
  const { memberCommsClassName, sidekickClassName, toggleTopPoptart } =
    usePoptartPositioning()

  // This effect is for figuring out basic information about where a user
  // is located when they load the app.
  useEffect(() => {
    const asyncFetchInitialSetup = async () => {
      // Initial setup gives us IP location info and some other feature configs.
      const initialSetupResponse = await fetchInitialSetup(flags)

      // default to CA for the rare instances that for some reason after clearing
      // your cache/cookies through google settings and starting a new browser
      // session, going to sidecarhealth.com first then clicking getQuote button
      // and going to /signup/medical, the call to initialSetup doesn't resolve
      // in time leaving no response, which leads to the state being undefined.
      const state = pathOr(
        'CA',
        ['payload', 'location', 'region'],
        initialSetupResponse,
      )

      // TVsquared does TV commercial tracking and this would violate CCPA and
      // GDPR laws in CA, so we have to let GTM know not to track them. by pushing
      // in the state no matter what, we have the flexibility to do that for
      // CA and perhaps in the future for any other states that have restrictions.
      // the extra check is because there have been some errors thrown where can't
      // push of undefined, so just want to make sure that dataLayer is there and is an array first
      if (window && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'userStateEvent',
          state: Object.keys(stateMapping).find(
            key => stateMapping[key] === state,
          ),
        })
      }
    }
    asyncFetchInitialSetup()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect for initial setup, deps are stable dispatch-bound actions
  }, [])

  // This effect is for fetching account and member information about the
  // authenticated user.
  useEffect(() => {
    if (!token) return

    // Don't do this async, just call it now. This will prompt the user to
    // allow us to use the devices location. Once we have it, we'll store
    // it in the Location store. It's more accurate than the IP location
    // that we'll get from the initial setup response.
    getCurrentLocation()

    const fetchAccountAndMemberInfo = async () => {
      // if the user can be logged in, they have "members" in their account
      // so we should fetch for those.
      const result = await fetchMembers()

      // Sometimes a user times out or their token is no longer valid. Here in
      // the UI, we won't know that information until we make a backend call
      // and they verify the session against what's stored in the DB.
      // If the above API call returned as 401 Unauthorized, we don't want to
      // proceed any further so just return early and this will help us from
      // adding more noise to trackjs analytics than necessary. It'll also
      // clear any chats that might be lying around.
      if (result?.type === 'auth/FORCE_LOGOUT') return

      // User already logged-in and not timed-out. Set memberUuid
      datadogLogs.setUser({ id: webStorage.getItem('sub') })

      // we need as much info about the authenticated user as possible
      // fetch this AFTER we get the list for the account so as not to
      // cause members.entities to become non-empty but only partially full.
      // In the cases where the admin has linked to the member app, but is
      // not quite impersonating a member, we won't have a member to work with
      // so we can't really make this call without throwing errors. In those
      // cases, we won't have a memberUuid saved as the `sub`.
      if (webStorage.getItem('sub')) {
        fetchAuthedUser()
      }

      // Check if the accountMetadata is empty and if user just registered
      // to prevent an unnecessary call when app is loaded on signup.
      // `justRegistered` will be null if user has not logged in won't be
      // called if user hasn't logged in. If empty on a refresh fetch again
      const justRegistered = parseBool(webStorage.getItem('justRegistered'))
      if (and(isEmpty(accountMetadata), equals(justRegistered, false))) {
        fetchAccountMetaData()
      }

      // Fetch inbox unresolved count only after we've confirmed the session
      // is valid (fetchMembers didn't 401). This prevents excessive 401s from
      // firing against expired tokens on cold startup. WEB-1787
      const hasPolicy = webStorage.getItem('hasPolicy') === 'true'
      const phiUuidList =
        JSON.parse(webStorage.getItem('accountMemberAccessMap'))?.PHI || []
      if (hasPolicy && phiUuidList.length > 0) {
        fetchUnresolvedMessages()
      }
    }
    fetchAccountAndMemberInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs only on token change; other deps are stable dispatch-bound actions
  }, [token])

  // this handler is trying to be the de-facto handler for logging out.
  // Right now it's working for the global nav and the help nav but the
  // MobileGlobalNav is using a different handler for now.
  //TODO: make the MobileGlobalNav use this handler by moving it to this
  // component and only showing it when the user is in a flow.
  const handleLogout = async () => {
    const result = await logout()
    if (result.error) {
      datadogLogs.logger.error('Logout error', {
        error: result.error,
        message: 'Error logging out',
      })
      forceLogout()
    }
    navigate('/logout')
  }

  // there's always going to be a global nav, but the items in it will change
  // depending on if the user has a policy or not. If they have a policy they
  // get the full nav and if they don't have a policy; but are logged in (like
  // they're continuing the sign up process), they'll get just the links to
  // get them out of the flow or signout or whatever.
  let mainNavItems = []
  const globalNavItems = getGlobalNavItems({
    handleLogout,
    accountMetadata,
    policy,
    selectedMemberUuid,
    canViewExpenses,
  })
  // webStorage.getItem('hasPolicy') returns a string, not boolean. Must be evaluated as a string.
  const hasPolicy = webStorage.getItem('hasPolicy') === 'true'
  if (hasPolicy) {
    mainNavItems = mainNavItems.concat(globalNavItems)
  }

  // Hand these labels to the static app shell in `index.html`, which paints the
  // app's chrome during `?splash=false` crossings in from member-app (#MX-5286).
  const navShapeKey = getNavShapeKey(mainNavItems)
  useEffect(() => {
    cacheNavShape(navShapeKey)
  }, [navShapeKey])

  // This loads things like TrackJS and soce Facebook stuff
  handle3rdPartyScripts()

  // we allow member care admins to impersonate users to help search for care,
  // but we also don't want member care to be able to just see all of the info
  // for a member so we block certain pages from being viewed. Care and doctors
  // are for searching for care/help, dashboard is here because it's helpful in
  // cases like reapply for dependents.
  //
  // Storage writes happen synchronously during render so the token and admin
  // flags are available before PrivateRoutes evaluates auth. The navigate call
  // (to clean sensitive params from the URL) runs in a useEffect to avoid
  // updating Router state during render.
  setImpersonationStorage(location)
  useEffect(() => {
    cleanImpersonationParams(location, navigate)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when search params change; location/navigate refs are stable
  }, [location.search])
  const isImpersonatingAdmin = webStorage.getItem('isAdmin') === 'true'
  // login and logout stay reachable so an impersonation tab can end instead of
  // sitting on NotFound.
  const adminAllowedPattern = canViewExpenses
    ? /\/(care|dashboard|doctors|expenses|help|login|logout|preview-coverage|pharmacy)\/?/
    : /\/(care|dashboard|doctors|help|login|logout|preview-coverage|pharmacy)\/?/
  const isAdminAllowed =
    !isImpersonatingAdmin ||
    (isImpersonatingAdmin && adminAllowedPattern.test(location.pathname))

  // These styles set up the outermost wrapper for the app. They ensure that
  // the content is set up to fit and adapt to any device it shows on. The
  // classes for flex-col set up the app so that the global nav is on top on
  // desktop and at the bottom for mobile. The baseline styles are for setting
  // up our stacking order and ensuring that content can't spill out and cause
  // random scrollbars.
  const mainCanvasStyle = () =>
    classify({
      layout: 'relative flex h-full max-w-full',
      ordering: 'flex-col-reverse md:flex-col',
      baselines: 'z-0 overflow-hidden',
    })

  // These styles are for the main content area. They ensure that the content
  // stretches to fill the rest of the page (below the header) and that it can
  // scroll as necessary. The next level down, inside of this wrapper is where
  // the pages will be rendered. Those pages may be wrapped in HTML or in a
  // React.Fragment depending on their needs.
  const mainContentStyle = classify({
    layout: 'relative flex flex-1 flex-col h-full max-w-full',
    scrolling: 'overflow-auto',
  })

  // The global nav should be blue on the login and forgot password pages so it bleeds into the page body.
  // This mirrors the MobileGlobalNav and the marketing site.
  const navSentiment =
    location.pathname === '/login' || location.pathname === '/forgot'
      ? Sentiment.PRIMARY
      : undefined

  return (
    <div className={mainCanvasStyle()}>
      {/* Has to sit outside the nav menus so accessiBe can bind to it. */}
      <AccessibeTrigger />

      <GlobalNav
        authenticated={token}
        isNativeApp={isNativeApp}
        mainNavItems={mainNavItems}
        sentiment={navSentiment}
      >
        <HelpNav
          className="max-md:hidden"
          authenticated={token}
          logout={handleLogout}
          hasPolicy={hasPolicy}
          unresolvedRequests={unresolvedRequests}
          sentiment={navSentiment}
        />
      </GlobalNav>

      <div id="appContent" className={mainContentStyle}>
        {/* Rather than blocking each page individually, we do this once here */}
        {!isAdminAllowed ? (
          <NotFound />
        ) : (
          <>
            <ErrorBoundary key={location.pathname}>
              <Suspense
                fallback={
                  <ContentSection
                    breathable
                    className="flex justify-center items-center animate-fade-in"
                  >
                    <Spinner size="xxl" />
                  </ContentSection>
                }
              >
                {flags['scroll-to-top'] && (
                  <ScrollToTop dependencies={[location.pathname]} />
                )}
                <Routes>
                  {/* public pages */}
                  <Route path="forgot" element={<ForgotPassword />} />
                  <Route path="login" element={<Login />} />
                  <Route path="logout" element={<Logout />} />
                  <Route path="notAvailable" element={<NotAvailable />} />
                  <Route path="reset" element={<ResetPassword />} />
                  <Route path="thankyou" element={<ThankYou />} />

                  {/* EB Registration flow (keep signup before getQuote) */}
                  <Route
                    path="signup"
                    element={() => <Navigate to="notAvailable" replace />}
                  />
                  <Route
                    path="getQuote"
                    element={() => <Navigate to="notAvailable" replace />}
                  />

                  {/* GFI/ASO Registration flow */}
                  <Route path="register/*" element={<RegisterAccountFlow />} />
                  <Route
                    path="link-payment-method"
                    element={<LinkPaymentMethod />}
                  />
                  <Route path="thank-you" element={<RegistrationSuccess />} />
                  {/* private pages  */}
                  {/* All private routes require login */}
                  <Route element={<PrivateRoutes />}>
                    <Route path="account/*" element={<Account />} />

                    <Route
                      path="cards/:memberUuid?"
                      element={<BenefitCard />}
                    />

                    <Route
                      path="care/*"
                      element={
                        flags['member-app-bundles'] ? <Bundles /> : <CareV2 />
                      }
                    />
                    {/**
                     * Leaving this in, useful for testing purposes while developing, remove after
                     * 10/1/2025
                     */}
                    {/* <Route
                    path="oldcare/*"
                    element={<CareV2 />}
                    key={window.location.pathname}
                  /> */}
                    {/* Once all Access plans expire, we can remove this path */}
                    <Route path="previewCoverage" element={<CareV2 />} />
                    <Route path="accountMenu" element={<MobileAccountMenu />} />
                    <Route
                      path="dashboard/:memberUuid?"
                      element={<Dashboard />}
                    />
                    <Route path="doctors/*" element={<Doctors />} />
                    <Route path="expenses/*" element={<Expenses />} />
                    <Route
                      path="pharmacy/:pharmacyId"
                      element={<PharmacyDetail />}
                    />
                    <Route path="help/*" element={<Help />} />
                    <Route path="idcard" element={<IdCard />} />
                    <Route path="inbox/*" element={<Inbox />} />
                    <Route path="printCoverage" element={<PrintCoverage />} />

                    {/* The preview-coverage page is private because if users
                        make too many requests, it could cause a huge contentful
                        bill, or possibly a DOS attack with the care service.
                        This page is only intended for Member Care admins.
                     */}
                    <Route
                      path="preview-coverage/:group?"
                      element={
                        flags['arch-group-preview-coverage'] ? (
                          <PreviewCoverage />
                        ) : (
                          <NotFound />
                        )
                      }
                    />

                    {/* private flows */}
                    {/* we need the :step/:uuid version to not mistake the step for the uuid */}
                    <Route
                      path="addEBMember/:step/:uuid"
                      element={() => <Navigate to="notAvailable" replace />}
                    />
                    <Route
                      path="addEBMember"
                      element={() => <Navigate to="notAvailable" replace />}
                    />
                    <Route
                      path="coverPrimary/:step/:uuid"
                      element={() => <Navigate to="notAvailable" replace />}
                    />
                    <Route
                      path="coverPrimary/:uuid"
                      element={() => <Navigate to="notAvailable" replace />}
                    />
                    <Route
                      path="reapply/:step/:uuid"
                      element={() => <Navigate to="notAvailable" replace />}
                    />
                    <Route
                      path="reapply/:uuid"
                      element={() => <Navigate to="notAvailable" replace />}
                    />
                    <Route
                      path="reenroll/:step/:uuid"
                      element={() => <Navigate to="notAvailable" replace />}
                    />
                    <Route path="reinstate/:uuid" element={<ReinstateFlow />} />

                    {/* private defaults */}
                    <Route
                      path="/"
                      element={<Navigate to="dashboard" replace />}
                    />
                    <Route element={<NotFound trackAs404 />} />
                  </Route>

                  {/* public defaults */}
                  <Route path="/" element={<Navigate to="login" replace />} />
                  <Route path="*" element={<NotFound trackAs404 />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>

            {/* this is where the modals will be rendered */}
            <div id="rootModal" />

            {/* All toasts come from here */}
            <Toast toasts={toasts} />
          </>
        )}
      </div>

      <div
        id="popTartWrapper"
        className={classify({
          flex: 'flex flex-row-reverse items-end gap-4',
          basicPosition: 'fixed z-[667] bottom-0',
          mobilePosition: 'max-xs:w-full max-xs:mx-auto',
          desktopPosition: 'xs:right-6 xs:left-6',
          // without this, the background will not be interactive while the wrapper is open
          enableBackground: 'pointer-events-none',
        })}
      >
        <MemberCommsPoptart
          // clicking the poptart puts it in "focus" which means it gets a higher
          // z-index in the stack on mobile.
          className={memberCommsClassName}
          onClick={() => toggleTopPoptart('memberComms')}
        />

        {flags['sidecar-sidekick'] && (
          <SidekickPoptart
            // clicking the poptart puts it in "focus" which means it gets a higher
            // z-index in the stack on mobile.
            className={sidekickClassName}
            onClick={() => toggleTopPoptart('sidekick')}
          />
        )}
      </div>
    </div>
  )
}

export const App = connect(
  ({ Account, Auth, Notification, Member, MemberCommunication, Policy }) => ({
    accountMetadata: Account?.accountMetadata?.entity || {},
    policy: Policy?.policy?.entity || {},
    poptartHistory: MemberCommunication.requestFlows?.history,
    requestTitle: MemberCommunication.requestType?.entity?.title,
    selectedMemberUuid: Member?.selectedMemberUuid,
    toasts: Notification?.toasts || [],
    token: Auth.authToken,
    unresolvedRequests: MemberCommunication.unresolved?.entity?.unresolved,

    // By paying attention to authedUserBusy, it causes the app to re-render
    // which also causes the HelpNav to re-render which makes sure that the
    // right phone number shows up in the HelpNav. Without this, the phone
    // number would only be the one for everyone/EB unless the user refreshed
    // the page. TODO: we may not need this anymore.
    authedUserBusy: Member.authedUserBusy || false,
  }),
  {
    clearPopTartHistory,
    clearNewMessage,
    clearRequestType,
    fetchInitialSetup,
    getCurrentLocation,
    loginFromLocal,
    logout,
    fetchGroupInfo,
    fetchAccountMetaData,
    fetchApplicants,
    fetchMembers,
    fetchAuthedUser,
    fetchUnresolvedMessages,
    forceLogout,
    prevHistory,
  },
)(BaseApp)
